/**
 * The create-recording envelope (contract 4.1.4, proposal v0.3), built from
 * form state.
 *
 * Sibling of ./recordingOpBuilders, and pure for the same reason — no React, no
 * JSX, so it stays in jest's fast DOM-less project and any surface can import
 * it.
 *
 * ## Why this is not an op builder
 *
 * Every other write in this form is a diff: `(current, original)` in, a list of
 * `add`/`delete`/`edit` ops out. A create has no original, so the payload is
 * **flat** — `create-payload-flat` in the frontend spec §6.1. Emitting ops here
 * would mean every field carrying `Add`, which encodes nothing and would give
 * the backend a second grammar to parse for no gain.
 *
 * The shared ground with the op builders is therefore the *inputs*, not the
 * output: this module takes the same `RecordingValues` bag the diff builders
 * take, reuses `parseContributionEvidenceNotes` so a contribution's evidence
 * URL and notes are unpacked in exactly one place, and reads the same
 * `confirmedDuplicates` map `buildIsrcEdits` reads so a reassigned ISRC rides
 * the same `dupIsrcs` rider on both writes. If any of those inputs change
 * shape, both writes change together.
 *
 * ## No tags
 *
 * v0.1 tagged each artist with `kind` and the release with `mode`. v0.3 drops
 * both: an entry that carries an id (`artistId`, `releaseId`) already exists
 * and is linked; one that carries a `sourceId` instead does not and is created.
 * The backend requires exactly one of the two, so the presence test is the
 * contract, not a convention layered over it (`create-artists-by-id-presence`,
 * `create-release-by-id-presence`).
 *
 * ## What it deliberately does not do
 *
 * No validation. The mandatory-set checklist (spec §4.2) gates the submit
 * button, and the backend re-runs every rule of 4.1.4.4 regardless; a third
 * copy of the rules here would be a third thing to keep in step.
 */
import {
  parseContributionEvidenceNotes,
  type ArtistEntryLike,
  type DuplicateIsrcSource,
  type RecordingValues,
} from './recordingOpBuilders';
import type {
  CreateRecordingEnvelope,
  CreateRecordingEnvelopePayload,
  CreateRecordingEnvelopePayloadContributionsInner,
} from 'shared_components/generated/admin';

/**
 * The payload's three sections, read off the payload type rather than
 * imported by name. The backend declares the same nested serializers under
 * the create payload and under the lookup's `existing` (WMC-557: the lookup
 * answers in the create payload's shapes), and the generator dedups
 * structurally identical schemas under whichever parent it meets first — so
 * the file names these live in are an accident of ordering, and the payload
 * type is the one stable handle.
 */
export type CreateRecordingPayloadRecording = NonNullable<
  CreateRecordingEnvelopePayload['recording']
>;
export type CreateRecordingPayloadArtist = NonNullable<
  CreateRecordingEnvelopePayload['artists']
>[number];
export type CreateRecordingPayloadRelease = NonNullable<
  CreateRecordingEnvelopePayload['release']
>;

/**
 * The one wire type, narrowed per arm. The generated payload has to admit
 * both arms, so `recording`, `artists` and `contributions` are all optional
 * on it; each builder promises the keys its arm always carries (and, on the
 * add arm, the absence of the create-only ones), so a caller reading the
 * result is not made to null-check what the builder guarantees.
 */
type EnvelopeOf<Payload extends CreateRecordingEnvelopePayload> = Omit<
  CreateRecordingEnvelope,
  'payload'
> & { payload: Payload };

/** A create: the recording block is always there, the existing id never. */
export type CreateArmEnvelope = EnvelopeOf<
  CreateRecordingEnvelopePayload & {
    recording: CreateRecordingPayloadRecording;
    artists: CreateRecordingPayloadArtist[];
    contributions: CreateRecordingEnvelopePayloadContributionsInner[];
    existingRecordingId?: undefined;
  }
>;

/** An add: the existing id and the contributions, and no `recording`,
 *  `artists` or `release` (`add-no-recording-block`, `add-no-artists`,
 *  `add-no-release-block`). */
export type AddArmEnvelope = EnvelopeOf<
  CreateRecordingEnvelopePayload & {
    existingRecordingId: string;
    contributions: CreateRecordingEnvelopePayloadContributionsInner[];
    recording?: undefined;
    artists?: undefined;
    release?: undefined;
  }
>;

/**
 * The one source vocabulary, shared by recordings, releases and (its first two
 * members) artists. Mirrors the backend's `SOURCE_CHOICES`; the generated enum
 * is the authority, this is the readable list the form's select is built from.
 */
export const RECORDING_SOURCES = [
  { value: 'discogs', label: 'Discogs' },
  { value: 'musicbrainz', label: 'MusicBrainz' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'deezer', label: 'Deezer' },
  { value: 'ppl', label: 'PPL' },
  { value: 'other', label: 'Other' },
] as const;

export type RecordingSource = (typeof RECORDING_SOURCES)[number]['value'];

/**
 * The release as `ChangeReleaseModal(mode='select')` hands it back.
 *
 * Declared structurally rather than imported from `ChangeReleaseModal.tsx` —
 * same reason `ArtistEntryLike` is declared in ./recordingOpBuilders rather
 * than imported from `ArtistEditorModal.tsx`. `ReleaseSelection` is assignable
 * to this.
 */
export interface ReleaseSelectionLike {
  /** The WMC release id, when the lookup found one. `null` means the release
   *  is not in the graph yet and has to be created from the metadata below. */
  releaseId: string | null;
  source: string;
  sourceId: string;
  releaseTitle: string;
  dateReleased: string | null;
  releaseLabels: string[];
  catNumbers: string[];
  mediaType: string;
  format: string;
  barcode?: string;
  sourceReleaseIds?: string[];
}

const trimmed = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** `''` where the field was never filled in — the backend treats absent and
 *  empty alike, and `null` would be a third state nothing means. */
const optional = (value: unknown): string | undefined =>
  trimmed(value) || undefined;

/**
 * The form's featured status, in the contract's vocabulary.
 *
 * The form says `nf` where the read model and the contract say `nfa`; create
 * is the one write where a non-featured artist is sent at all (the edit-add
 * arms stay `fa`/`ofa` — a non-featured artist on an existing recording is
 * demoted, not added). Anything unset is `fa`, the `create-default-featured`
 * default.
 */
const featuredStatusOf = (
  artist: Pick<ArtistEntryLike, 'featuredStatus'>
): CreateRecordingPayloadArtist['featuredStatus'] => {
  if (artist.featuredStatus === 'ofa') return 'ofa';
  if (artist.featuredStatus === 'nf') return 'nfa';
  return 'fa';
};

/**
 * One artist, identified by whichever id it has.
 *
 * `pendingCreate` is the discriminator: an artist picked from lookup exists in
 * the graph and carries an `artistId`; one typed in by the operator carries
 * the source it was found at instead. That is the same predicate
 * `buildCreateArtistEdits` splits on in edit mode, so the two flows cannot
 * disagree about which artists are new.
 *
 * `stageName` and `members` travel on **both** arms, as they do on the edit
 * payloads (4.1.3 and 4.4.2). The backend's link transform builds the band
 * artist name from `stageName` and reads group-or-persona off whether
 * `members` is empty, so an existing band sent by id alone would be linked as
 * a nameless persona. `members` is always present — `[]` says "no lineup",
 * where an absent key says nothing.
 */
const artistEntry = (
  artist: ArtistEntryLike
): CreateRecordingPayloadArtist => {
  // A member with no `exists` flag takes the arm's own answer. A linked
  // artist's lineup was read off the graph, so its members exist; a new
  // artist's lineup came from its source, so they do not until flagged.
  // Same defaults as the two arms in `bulkEditBatch`.
  const shared = (existsByDefault: boolean) => ({
    featuredStatus: featuredStatusOf(artist),
    stageName: artist.name,
    members: (artist.members ?? []).map((m) => ({
      id: m.id,
      stageName: m.stageName,
      _exists: m.exists ?? existsByDefault,
    })),
  });

  if (artist.pendingCreate) {
    return {
      ...shared(false),
      sourceId: artist.pendingCreate.sourceId,
      source: artist.pendingCreate.source,
    };
  }

  return {
    ...shared(true),
    // An artist from lookup always has an id; the name is the fallback the
    // artist ops use for the same reason, so the two stay consistent.
    artistId: artist.id || artist.name,
  };
};

/**
 * The release, flat, identified by whichever id it has.
 *
 * A release the lookup found carries its `releaseId` and is linked; one the
 * operator typed in because the lookup returned nothing carries the
 * `sourceId` it will be created under. The backend accepts exactly one of the
 * two, so a selection that happens to hold both (a found release also knows
 * its source id) sends only `releaseId`. The metadata travels on both arms:
 * the backend checks the recording's year against `dateReleased` either way,
 * and requires `releaseTitle` on the link arm. `source` travels on both arms
 * too, because the recording's source has to match it (4.1.4.4).
 */
/**
 * The source ids a linked release carries. The link arm cannot send `sourceId`
 * (the backend takes exactly one of it or `releaseId`), so without this the
 * release's id at its source never reaches the indexed `sourceReleaseId`,
 * which the discography read requires. The lookup's ids when it returned any,
 * else the id the operator looked the release up by.
 */
const linkedSourceReleaseIds = (selection: ReleaseSelectionLike): string[] => {
  const fromLookup = (selection.sourceReleaseIds ?? [])
    .map(trimmed)
    .filter(Boolean);
  if (fromLookup.length > 0) return fromLookup;
  const sourceId = trimmed(selection.sourceId);
  return sourceId ? [sourceId] : [];
};

const releaseEntry = (
  selection: ReleaseSelectionLike
): CreateRecordingPayloadRelease => {
  const metadata = {
    source: selection.source as CreateRecordingPayloadRelease['source'],
    releaseTitle: selection.releaseTitle,
    dateReleased: selection.dateReleased,
    releaseLabels: selection.releaseLabels,
    catNumbers: selection.catNumbers,
    mediaType: selection.mediaType,
    format: selection.format,
    barcode: optional(selection.barcode),
  };

  if (selection.releaseId) {
    return {
      releaseId: selection.releaseId,
      sourceReleaseIds: linkedSourceReleaseIds(selection),
      ...metadata,
    };
  }
  return { sourceId: selection.sourceId, ...metadata };
};

const contributionEntry = (
  contribution: RecordingValues['contributions'][number]
): CreateRecordingEnvelopePayloadContributionsInner => {
  const { evidenceUrl, notes } = parseContributionEvidenceNotes(
    contribution.confidencePattern ?? ''
  );
  return {
    role: contribution.contribution ?? '',
    personaId: contribution.personaId || undefined,
    // `create-default-confidence`: '1' is Verified, the same default the edit
    // form's contribution payload applies.
    dataCategory: contribution.confidenceCategory ?? '1',
    sourceUrl: evidenceUrl || undefined,
    notes: notes || undefined,
  };
};

/**
 * The `dupIsrcs` entries: one per recording ISRCs are being taken from, naming
 * those ISRCs. On an edit the rider hangs off the individual ISRC's `add`, so
 * the ISRC is implicit; a create has no ops, so each entry carries its ISRCs,
 * and a recording that two of them come from appears once, listing both.
 * Only ISRCs actually in the list count — an ISRC confirmed and then removed
 * again is not being reassigned.
 */
const dupIsrcsOf = (
  isrcs: string[],
  confirmedDuplicates: Record<string, DuplicateIsrcSource[]>
): CreateRecordingPayloadRecording['dupIsrcs'] => {
  const bySource = new Map<
    string,
    NonNullable<CreateRecordingPayloadRecording['dupIsrcs']>[number]
  >();
  isrcs.forEach((isrc) =>
    (confirmedDuplicates[isrc] ?? []).forEach((d) => {
      const key = JSON.stringify([d.recordingId, d.documentId]);
      const entry = bySource.get(key);
      if (entry) {
        if (!entry.isrcs.includes(isrc)) entry.isrcs.push(isrc);
      } else {
        bySource.set(key, {
          isrcs: [isrc],
          dupRecordingId: d.recordingId,
          dupDocumentId: d.documentId,
        });
      }
    })
  );
  return bySource.size > 0 ? [...bySource.values()] : undefined;
};

export interface CreateEnvelopeInput {
  values: RecordingValues;
  /** Where the recording was found (`create-source-required`). */
  source: RecordingSource;
  /** Its id at that source — a MusicBrainz MBID, a Spotify track id. Optional:
   *  an `other` recording has none. */
  sourceId?: string;
  /** `null` is a legal input — a recording may be created without a release
   *  (`create-release-optional`). The field is simply omitted. */
  release: ReleaseSelectionLike | null;
  /** ISRC → the recordings it is being reassigned from, as the form's
   *  Reassign-ISRC confirmation accumulates them. */
  confirmedDuplicates?: Record<string, DuplicateIsrcSource[]>;
}

/**
 * The whole envelope, in one call (`create-single-envelope`).
 *
 * `user_id` and `westbury_id` are deliberately absent: the backend takes
 * identity from the session and ignores anything the payload claims. Sending
 * them would look like they were being honoured.
 */
export const buildCreateRecordingEnvelope = ({
  values,
  source,
  sourceId,
  release,
  confirmedDuplicates = {},
}: CreateEnvelopeInput): CreateArmEnvelope => {
  const isrcs = values.isrcs;
  return {
    payload: {
      recording: {
        recordingTitle: trimmed(values.formData.title),
        duration: optional(values.formData.duration),
        live: optional(values.formData.live),
        yearRecorded: optional(values.formData.yearRecorded),
        countryOfRecording: optional(values.formData.location),
        source,
        recordingId: optional(sourceId),
        recordingVersion: values.versions.map((v) => v.trim()).filter(Boolean),
        isrcs,
        pNames: values.pNames,
        dupIsrcs: dupIsrcsOf(isrcs, confirmedDuplicates),
      },
      artists: values.artists.map(artistEntry),
      contributions: values.contributions.map(contributionEntry),
      release: release ? releaseEntry(release) : undefined,
    },
  };
};

export interface AddEnvelopeInput {
  /** The WMC id of the Recording that already exists, from the existence
   *  lookup (`add-existing-recording-id`). */
  existingRecordingId: string;
  contributions: RecordingValues['contributions'];
}

/**
 * The add-arm envelope (contract 4.1.4.2g, WMC-557), in one call.
 *
 * The arm is told apart from a create by its keys, not by a tag: a payload
 * with `existing_recording_id` and no `recording` is an add. So this sends
 * exactly two things — the id and this artist's contributions — and nothing
 * of what the create arm sends. No `recording` block: the Recording's data
 * already exists and the add must not restate it (`add-no-recording-block`).
 * No `artists`: the Recording's lineup is what it is, and which of this
 * client's aliases the entry belongs to is the backend's to read off the
 * contributions' personas (`add-no-artists`). No `release`: the Recording's
 * release is whatever it already has (`add-no-release-block`). Each of the
 * three beside `existing_recording_id` is a 400.
 */
export const buildAddRecordingEnvelope = ({
  existingRecordingId,
  contributions,
}: AddEnvelopeInput): AddArmEnvelope => ({
  payload: {
    existingRecordingId,
    contributions: contributions.map(contributionEntry),
  },
});
